import { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Centralized error logger so failures are visible in the console (and easily
// re-routed to Sentry later) instead of being silently swallowed.
function logFetchFailure(scope, err) {
  // Network errors / 5xx are surfaced; 404s are normal during initial seeding
  if (err?.response?.status === 404) return;
  // eslint-disable-next-line no-console
  console.warn(`[useDynamicContent] ${scope} failed:`, err?.message || err);
}

/**
 * Fetches dynamic content for a page and merges it over the provided defaults.
 * - If the API request fails or returns empty/missing fields, the defaults are
 *   preserved so the public website NEVER renders blank.
 * - Strings, arrays, and objects are all supported. CMS values override defaults
 *   only when they are non-empty.
 */
export function useDynamicContent(pageName, defaults) {
  const [sections, setSections] = useState(defaults);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/public/content/${pageName}`);
        const remote = res?.data?.sections || {};
        const merged = { ...defaults };
        Object.keys(remote).forEach((k) => {
          const v = remote[k];
          if (v === undefined || v === null) return;
          if (typeof v === 'string' && v.trim() === '') return;
          if (Array.isArray(v) && v.length === 0) return;
          merged[k] = v;
        });
        if (active) setSections(merged);
      } catch (err) {
        logFetchFailure(`content/${pageName}`, err);
      }
    })();
    return () => { active = false; };
    // `defaults` is intentionally excluded — it's a new object reference on every
    // render but contains stable content. Including it would cause infinite refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageName]);

  return sections;
}

export function usePublicSettings(defaults = {}) {
  const [settings, setSettings] = useState(defaults);
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/public/settings`);
        if (active && res?.data) {
          const merged = { ...defaults };
          Object.entries(res.data).forEach(([k, v]) => {
            if (v && (typeof v !== 'string' || v.trim() !== '')) merged[k] = v;
          });
          setSettings(merged);
        }
      } catch (err) {
        logFetchFailure('settings', err);
      }
    })();
    return () => { active = false; };
    // `defaults` excluded for the same reason as useDynamicContent above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return settings;
}

export function usePublicJobs(defaults = []) {
  const [jobs, setJobs] = useState(defaults);
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/public/jobs`);
        if (active && Array.isArray(res?.data) && res.data.length > 0) setJobs(res.data);
      } catch (err) {
        logFetchFailure('jobs', err);
      }
    })();
    return () => { active = false; };
    // `defaults` excluded — stable content, new reference each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return jobs;
}

export function usePublicDocuments() {
  const [docs, setDocs] = useState([]);
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/public/documents`);
        if (active && Array.isArray(res?.data)) setDocs(res.data);
      } catch (err) {
        logFetchFailure('documents', err);
      }
    })();
    return () => { active = false; };
  }, []);
  return docs;
}

export function usePublicSeo(pageName) {
  const [overrides, setOverrides] = useState(null);
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/public/seo/${pageName}`);
        if (active && res?.data) setOverrides(res.data);
      } catch (err) {
        logFetchFailure(`seo/${pageName}`, err);
      }
    })();
    return () => { active = false; };
  }, [pageName]);
  return overrides;
}
